/*
 * Copyright (C) 2020 Reciprocity, Inc - All Rights Reserved
 * Unauthorized use, copying, distribution, displaying, or public performance
 * of this file, via any medium, is strictly prohibited. All information
 * contained herein is proprietary and confidential and may not be shared
 * with any third party without the express written consent of Reciprocity, Inc.
 */

import { map } from 'lodash';
import { ActionContext, ActionTree, GetterTree, Module, MutationTree } from 'vuex';
import createLogger from 'vuex/dist/logger';

import { Modal } from '@/components/zen_modal/types';
import { RootState } from '@/state/types';

const debug = process.env.NODE_ENV !== 'production';

export enum Category {
  Done = 'done',
  Todo = 'todo',
  Upcoming = 'upcoming',
}

export enum ObjectType {
  Assessment = 'assessment',
  Request = 'request',
  Task = 'task',
}

export enum ValidationState {
  Invalid,
  Valid,
  ValidZero,
}

export type CategoriesObjectCounts = { [key in keyof Category]: number };
export type ObjectTypesObjectCounts = { [key in keyof ObjectType]: number };

interface ParamsValidity {
  category: boolean;
  objectType: boolean;
}

interface State {
  stateMeta: {
    invalid: ParamsValidity;
    loading: boolean;
  };
  category: string;
  objectType: string;
  categories: Array<Category>;
  objectTypes: Array<ObjectType>;
  categoriesObjectCounts: CategoriesObjectCounts | {};
  objectTypesObjectCounts: ObjectTypesObjectCounts | {};
}

const state: State = {
  stateMeta: {
    invalid: {
      category: false,
      objectType: false,
    },
    loading: false,
  },
  category: '',
  objectType: '',
  categories: [Category.Todo, Category.Upcoming, Category.Done],
  objectTypes: [ObjectType.Task, ObjectType.Request, ObjectType.Assessment],
  // TODO: go with null to indicate unfetched
  categoriesObjectCounts: {
    [Category.Todo]: 0,
    [Category.Upcoming]: 0,
    [Category.Done]: 0,
  },
  // TODO: go with null to indicate unfetched
  objectTypesObjectCounts: {
    [Category.Todo]: {
      tasks: 0,
      requests: 0,
      assessments: 0,
    },
    [Category.Upcoming]: {
      tasks: 0,
      requests: 0,
      assessments: 0,
    },
    [Category.Done]: {
      tasks: 0,
      requests: 0,
      assessments: 0,
    },
  },
};

export const SET_LOADING_STATE = 'SET_LOADING_STATE';
export const SET_VALIDATION_STATE = 'SET_VALIDATION_STATE';
export const SET_CATEGORY = 'SET_CATEGORY';
export const SET_OBJECT_TYPE = 'SET_OBJECT_TYPE';
export const SET_CATEGORIES_OBJECT_COUNTS = 'SET_CATEGORIES_OBJECT_COUNTS';
export const SET_OBJECT_TYPES_OBJECT_COUNTS = 'SET_OBJECT_TYPES_OBJECT_COUNTS';

const mutations: MutationTree<State> = {
  SET_LOADING_STATE(state: State, loading: boolean) {
    state.stateMeta.loading = loading;
  },
  SET_VALIDATION_STATE(state: State, paramsValidity: ParamsValidity) {
    state.stateMeta.invalid = paramsValidity;
  },

  SET_CATEGORY(state: State, category: string) {
    state.category = category;
  },
  SET_OBJECT_TYPE(state: State, objectType: string) {
    state.objectType = objectType;
  },
  SET_CATEGORIES_OBJECT_COUNTS(state: State, counts: CategoriesObjectCounts) {
    state.categoriesObjectCounts = counts;
  },
  SET_OBJECT_TYPES_OBJECT_COUNTS(state: State, counts: ObjectTypesObjectCounts) {
    state.objectTypesObjectCounts = counts;
  },
};

const actions: ActionTree<State, RootState> = {
  setLoadingState({ commit }: ActionContext, loadingState: boolean) {
    commit(SET_LOADING_STATE, loadingState);
  },

  // TODO: this is a race cond
  setCategory({ commit, getters }: ActionContext, categoryInput: string) {
    commit(SET_CATEGORY, categoryInput);
    commit(SET_VALIDATION_STATE, {
      category: !getters.categories.includes(categoryInput),
      objectType: getters.isInvalidObjectTypeState,
    });
  },
  setObjectType({ commit, getters, rootGetters }: ActionContext, objectTypeInput: string) {
    const objectTypes = map(Object.values(ObjectType), t => rootGetters.modelMeta[t].humanPlural);

    commit(SET_OBJECT_TYPE, objectTypeInput);
    commit(SET_VALIDATION_STATE, {
      category: getters.isInvalidCategoryState,
      objectType: !objectTypes.includes(objectTypeInput),
    });
  },
  setCategoriesObjectCounts({ commit }: ActionContext, categoriesObjectCounts) {
    commit(SET_CATEGORIES_OBJECT_COUNTS, categoriesObjectCounts);
  },
  setObjectTypesObjectCounts({ commit }: ActionContext, objectTypesObjectCounts) {
    commit(SET_OBJECT_TYPES_OBJECT_COUNTS, objectTypesObjectCounts);
  },
  async openNewObjectModal(
    { dispatch, getters, rootState }: ActionContext,
    objectType: string = getters.objectTypeMeta.titleSingular
  ) {
    const modalConfig: Modal = {
      id: 'new_object',
      type: 'NewObject',
      data: {
        url: rootState.routes.sorNewObject.replace('__OBJECT_TYPE__', objectType),
        type: objectType,
      },
    };

    try {
      await dispatch('modals/show', modalConfig, { root: true });
      return true;
    } catch {
      // User canceled modal

      return false;
    }
  },
};

const getters: GetterTree<State, RootState> = {
  /**
   * Component state
   */
  loadingState(state: State): boolean {
    return state.stateMeta.loading;
  },
  validationState(_: State, getters: Getters): ValidationState {
    if (getters.isInvalidState) return ValidationState.Invalid;
    else if (!getters.objectTypeHasObjects) return ValidationState.ValidZero;
    return ValidationState.Valid;
  },
  isInvalidState(_: State, getters: Getters): boolean {
    return getters.isInvalidCategoryState || getters.isInvalidObjectTypeState;
  },
  isInvalidCategoryState(state: State): boolean {
    return state.stateMeta.invalid.category;
  },
  isInvalidObjectTypeState(state: State): boolean {
    return state.stateMeta.invalid.objectType;
  },
  isValidState(_: State, getters: Getters): boolean {
    return getters.validationState == ValidationState.Valid;
  },
  isValidZeroState(_: State, getters: Getters): boolean {
    return getters.validationState == ValidationState.ValidZero;
  },

  category(state: State): string {
    return state.category;
  },
  objectType(state: State): string {
    return state.objectType;
  },
  objectTypeMeta(_state: State, getters: Getters, _rootState: State, rootGetters: Getters) {
    return rootGetters.modelMetaFindBy('humanPlural', getters.objectType);
  },

  categories(state: State): Array<Category> {
    return state.categories;
  },
  objectTypes(state: State): Array<ObjectType> {
    return state.objectTypes;
  },

  /**
   * Object counts
   */
  categoriesObjectCounts(state: State): CategoriesObjectCounts {
    return state.categoriesObjectCounts;
  },
  objectTypesObjectCounts(state: State): ObjectTypesObjectCounts {
    return state.objectTypesObjectCounts;
  },

  /**
   * Helpers
   */
  categoryHasObjects(_: State, getters: Getters): boolean {
    return getters.categoriesObjectCounts[getters.category] > 0;
  },
  objectTypeHasObjects(_: State, getters: Getters): boolean {
    const objectTypesObjectCounts = getters.objectTypesObjectCounts[getters.category];

    return !!objectTypesObjectCounts && objectTypesObjectCounts[getters.objectType] > 0;
  },
};

export default (): Module<State, RootState> => ({
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
  strict: debug,
  plugins: debug ? [createLogger()] : [],
});
